import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Separate, independent session for the Lone Star cross-brand bridge.
 * Deliberately isolated from the Amigos AuthContext:
 * - own token key ('lstar_token'), stored in sessionStorage (not
 *   localStorage) so it does not persist beyond the browser tab/session
 * - own axios instance with its own Authorization header — never mutates
 *   the shared `axios.defaults` used by the Amigos dashboard
 * - own /api/me check (brand-agnostic endpoint) instead of Amigos' /auth/me
 */
const LoneStarAuthContext = createContext(null);

export const lonestarApi = axios.create({ baseURL: API });

export const LoneStarAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem('lstar_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      lonestarApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      lonestarApi.get('/me')
        .then((res) => setUser(res.data))
        .catch(() => { sessionStorage.removeItem('lstar_token'); setToken(null); setUser(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const setSession = (accessToken, userData) => {
    sessionStorage.setItem('lstar_token', accessToken);
    lonestarApi.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
  };

  const logout = () => {
    sessionStorage.removeItem('lstar_token');
    delete lonestarApi.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <LoneStarAuthContext.Provider value={{ user, token, setSession, logout, loading }}>
      {children}
    </LoneStarAuthContext.Provider>
  );
};

export const useLoneStarAuth = () => {
  const ctx = useContext(LoneStarAuthContext);
  if (!ctx) throw new Error('useLoneStarAuth must be inside LoneStarAuthProvider');
  return ctx;
};

export default LoneStarAuthContext;
